/* eslint-disable no-restricted-syntax -- status dots use a neutral gray (bg-gray-400) by design; not theme-dependent */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { integrationsApi } from '../../services/api';
import { useTimezone } from '../../contexts/TimezoneContext';
import type { DataSource } from '../../types';

const SOURCE_TYPE_LABELS: Record<string, string> = {
  plaid: 'Plaid',
  quickbooks: 'QuickBooks',
  xero: 'Xero',
  square: 'Square',
  shopify: 'Shopify',
  stripe: 'Stripe',
  paypal: 'PayPal',
  google_drive: 'Google Drive',
  clover: 'Clover',
  csv_upload: 'CSV Upload',
  manual: 'Manual',
  api: 'API',
  webhook: 'Webhook',
  mrpeasy: 'MRPeasy',
};

function getSourceLabel(type: string): string {
  return SOURCE_TYPE_LABELS[type] ?? type.charAt(0).toUpperCase() + type.slice(1);
}

const DOT_COLOR_CLASSES: Record<string, string> = {
  healthy: 'bg-success',
  warning: 'bg-warn',
  error: 'bg-destructive',
  disconnected: 'bg-gray-400',
};

function formatLastSync(date: Date): string {
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Max sources shown on narrow screens before collapsing to "+N more" */
const MOBILE_MAX_VISIBLE = 3;

const ConnectedSourcesBadge: React.FC = () => {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const { toUserTimezone } = useTimezone();

  useEffect(() => {
    integrationsApi.getDataSources()
      .then(({ dataSources }) => setSources(dataSources))
      .catch(() => setSources([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  // ── Empty state ──
  if (sources.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border border-warn/15 bg-warn/[0.03]">
        <span className="text-xs font-medium text-warn">
          No data sources connected
        </span>
        <span className="text-[10px] text-muted-foreground/60">&middot;</span>
        <Link
          to="/sources"
          className="text-xs font-medium text-primary underline underline-offset-2 hover:text-primary transition-colors"
        >
          Connect your first source
        </Link>
      </div>
    );
  }

  // ── Connected state ──
  const hiddenCount = sources.length > MOBILE_MAX_VISIBLE ? sources.length - MOBILE_MAX_VISIBLE : 0;

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-5 py-3.5 rounded-xl border border-white/[0.04] bg-white/[0.02] dark:border-white/[0.04] dark:bg-white/[0.02]">
      <span className="text-xs font-medium text-muted-foreground">Connected:</span>

      {sources.map((source, i) => {
        const dotClass = DOT_COLOR_CLASSES[source.healthStatus] ?? 'bg-gray-400';
        const tooltip = source.lastSync
          ? `Last sync: ${formatLastSync(toUserTimezone(new Date(source.lastSync)))}`
          : 'Never synced';

        return (
          <span
            key={source.id}
            className={`group relative flex items-center gap-1.5 text-xs font-medium text-muted-foreground/60 cursor-default${
              i >= MOBILE_MAX_VISIBLE ? ' hidden sm:flex' : ''
            }`}
          >
            {i > 0 && (
              <span className={`text-[10px] text-muted-foreground/60 mr-1${
                i >= MOBILE_MAX_VISIBLE ? ' hidden sm:inline' : ''
              }`}>
                &middot;
              </span>
            )}
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} />
            <span>{getSourceLabel(source.type)}</span>

            {/* Tooltip */}
            <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[11px] text-foreground opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10">
              {tooltip}
            </span>
          </span>
        );
      })}

      {/* "+N more" on mobile */}
      {hiddenCount > 0 && (
        <span className="flex items-center text-xs text-muted-foreground sm:hidden">
          <span className="text-[10px] mr-1">&middot;</span>
          +{hiddenCount} more
        </span>
      )}
    </div>
  );
};

export default ConnectedSourcesBadge;
